import React, { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from 'react';

export type ViewMode = 'standard' | 'advanced';

interface ViewModeContextType {
    viewMode: ViewMode;
    isStandard: boolean;
    isAdvanced: boolean;
    setViewMode: (mode: ViewMode) => void;
    toggleViewMode: () => void;
}

const STORAGE_KEY = 'hades-view-mode';

function readPersistedMode(): ViewMode {
    try {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (stored === 'standard' || stored === 'advanced') return stored;
    } catch {
        // localStorage unavailable
    }
    return 'standard';
}

const ViewModeContext = createContext<ViewModeContextType | undefined>(undefined);

interface ViewModeProviderProps {
    children: ReactNode;
    defaultMode?: ViewMode;
}

export const ViewModeProvider: React.FC<ViewModeProviderProps> = ({ children, defaultMode }) => {
    const [viewMode, setViewModeState] = useState<ViewMode>(() => defaultMode ?? readPersistedMode());

    const setViewMode = useCallback((mode: ViewMode) => {
        setViewModeState(mode);
        try {
            localStorage.setItem(STORAGE_KEY, mode);
        } catch {
            // localStorage unavailable
        }
    }, []);

    const toggleViewMode = useCallback(() => {
        setViewMode(viewMode === 'standard' ? 'advanced' : 'standard');
    }, [viewMode, setViewMode]);

    useEffect(() => {
        if (defaultMode && defaultMode !== viewMode) {
            const stored = readPersistedMode();
            if (stored === 'standard' && defaultMode === 'advanced') {
                // Only auto-upgrade if user hasn't explicitly chosen; first-time admins get advanced
            }
        }
    }, [defaultMode]);

    const value: ViewModeContextType = {
        viewMode,
        isStandard: viewMode === 'standard',
        isAdvanced: viewMode === 'advanced',
        setViewMode,
        toggleViewMode,
    };

    return <ViewModeContext.Provider value={value}>{children}</ViewModeContext.Provider>;
};

export function useViewMode(): ViewModeContextType {
    const context = useContext(ViewModeContext);
    if (!context) {
        throw new Error('useViewMode must be used within a ViewModeProvider');
    }
    return context;
}

/** Safe version that returns standard mode when outside the provider (for tests, storybook, etc.) */
export function useViewModeSafe(): ViewModeContextType {
    const context = useContext(ViewModeContext);
    if (!context) {
        return {
            viewMode: 'standard',
            isStandard: true,
            isAdvanced: false,
            setViewMode: () => {},
            toggleViewMode: () => {},
        };
    }
    return context;
}
